import { bindecode, binencode } from "@/lib/binary";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";
import Dexie, { Entity, type DexieOptions, type EntityTable } from "dexie";
import bin from "typed-binary";

export const WiredContactDetailsPayload = bin.object({
  address: bin.u8Array(32),
  preferredName: bin.string,
});

export const WiredContactDetails = bin.object({
  signature: bin.u8Array(64),
  payload: WiredContactDetailsPayload,
});

export class WalletEntity extends Entity<MobyStateRepo> {
  public name!: string;
  public address!: string;
  public proof!: string;
}

export class KeyValueEntity extends Entity<MobyStateRepo> {
  public key!: string;
  public value!: unknown;
}

export type Session = {
  name: string;
  address: string;
  secret: string;
};

export class MobyStateRepo extends Dexie {
  public wallets!: EntityTable<WalletEntity, "address">;
  public kvs!: EntityTable<KeyValueEntity, "key">;

  constructor(name: string, opts?: DexieOptions) {
    super(name, opts);

    this.version(1).stores({
      wallets: "&name, &address, &proof",
      kvs: "&key",
    });

    this.wallets.mapToClass(WalletEntity);
    this.kvs.mapToClass(KeyValueEntity);
  }

  async storeWallet(wallet: { name: string; address: string; proof: string }) {
    await this.wallets.add(wallet);
  }

  async getWallets() {
    return await this.wallets.toArray();
  }

  async getSession() {
    const entry = await this.kvs.get("session");
    if (entry === undefined) {
      return;
    }

    return entry.value as Session;
  }

  async setSession(session: Session) {
    await this.kvs.put({ key: "session", value: session });
  }

  async invalidateSession() {
    await this.kvs.delete("session");
  }
}

export async function generateWallet(password: string) {
  const proof = crypto.getRandomValues(new Uint8Array(32));
  const wallet = await deriveWallet(password, bytesToHex(proof));

  return {
    ...wallet,
    proof,
  };
}

export function signMessageWithSession(session: Session, message: Uint8Array) {
  return ed25519.sign(message, session.secret);
}

export async function deriveWallet(password: string, proof: string) {
  const proofBytes = hexToBytes(proof);

  if (proofBytes.byteLength !== 32) {
    throw new Error("Proof must be exactly 32 bytes.");
  }

  const encoder = new TextEncoder();

  const buffer = new Uint8Array("moby".length + password.length + proof.length);

  let offset = 0;

  buffer.set(encoder.encode("moby"), offset);
  offset += "moby".length;

  buffer.set(encoder.encode(password), offset);
  offset += password.length;

  buffer.set(proofBytes, offset);
  offset += proofBytes.length;

  const hash = await crypto.subtle.digest("SHA-256", buffer);
  if (hash.byteLength !== 32) {
    throw new Error(
      "crypto.subtle.digest SHA-256 did not return a 32 byte hash.",
    );
  }

  const secretKey = new Uint8Array(hash);
  const publicKey = ed25519.getPublicKey(secretKey);

  return { secretKey, publicKey };
}

export function createAndSignContactDetails(session: Session) {
  const payload: bin.Parsed<typeof WiredContactDetailsPayload> = {
    address: hexToBytes(session.address),
    preferredName: session.name,
  };

  const signature = ed25519.sign(
    binencode(WiredContactDetailsPayload, payload),
    session.secret,
  );

  return binencode(WiredContactDetails, {
    signature,
    payload,
  });
}

export function parseAndVerifyContactDetails(contactDetailsHex: string) {
  let contactDetails;
  try {
    contactDetails = hexToBytes(contactDetailsHex);
  } catch (err) {
    throw new Error("Contact details provided is not valid hex.", {
      cause: err,
    });
  }

  let parsed;
  try {
    parsed = bindecode(WiredContactDetails, contactDetails);
  } catch (err) {
    throw new Error("Contact details provided is malformed.", { cause: err });
  }

  if (!verifyContactDetails(parsed)) {
    throw new Error("Contact details provided has a malformed signature.");
  }

  return {
    address: bytesToHex(parsed.payload.address),
    preferredName: parsed.payload.preferredName,
    signature: bytesToHex(parsed.signature),
  };
}

export function verifyContactDetails(
  contactDetails: bin.Parsed<typeof WiredContactDetails>,
) {
  const { signature, payload } = contactDetails;

  return ed25519.verify(
    signature,
    binencode(WiredContactDetailsPayload, payload),
    payload.address,
  );
}
